import React, { useMemo } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { components } from "../../types/api";
import CampaignDetailsCard from "../card/CampaignDetailsCard";
import MetricsTimelineChart, {
  MetricDataPoint,
  MetricType,
} from "../card/MetricsTimelineChart";
import CreativeMetricsSection from "./CreativeMetricsSection";
import PublisherMetricsSection from "./PublisherMetricsSection";
import {
  cardGridSx,
  costPer,
  exportDailyMetrics,
  MetricsDateToolbar,
  PerformanceTable,
  ratio,
  rowsByCampaign,
  rowsByDay,
  SectionTitle,
  VideoRetention,
} from "./PaidMediaSections";
import {
  formatCurrency,
  formatDecimal,
  formatNumber,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";

type CampaignMetricsDTO = components["schemas"]["CampaignMetricsDTO"];

export type SocialNetwork = "meta" | "tiktok" | "reddit";

interface SocialMergeCampaignDetailsProps {
  network: SocialNetwork;
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
  metricsStartDate?: string;
  metricsEndDate?: string;
  onMetricsStartDateChange?: (value: string) => void;
  onMetricsEndDateChange?: (value: string) => void;
  onClearMetricsDateFilters?: () => void;
}

/**
 * Dashboard de cliente de paid social (Meta, TikTok y Reddit). Las tres redes
 * guardan lo mismo en las tablas comunes; cambia qué significa cada cifra:
 * - Meta: los clics son clics en el enlace y las conversiones dependen del
 *   objetivo del briefing (leads, compras o píxel).
 * - TikTok: las interacciones suman me gusta, comentarios, compartidos y seguidores.
 * - Reddit: no da interacciones, y sus "ubicaciones" son comunidades.
 *
 * Igual que en Google Ads, el coste es el de cliente; el alcance y los desgloses
 * de anuncios y ubicaciones son de toda la campaña.
 */
const SocialMergeCampaignDetails: React.FC<SocialMergeCampaignDetailsProps> = ({
  network,
  isLoadingMergedCampaign,
  mergedCampaignId,
  mergedCampaignData,
  metricsStartDate,
  metricsEndDate,
  onMetricsStartDateChange,
  onMetricsEndDateChange,
  onClearMetricsDateFilters,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const copy = {
    meta: {
      clicks: t("campaigns.social.linkClicks", { defaultValue: "Link clicks" }),
      conversionsHint: t("campaigns.social.metaConversionsHint", {
        defaultValue:
          "Counted according to the campaign goal: leads for CPL, purchases for CPA and pixel conversions otherwise.",
      }),
      engagementsHint: t("campaigns.social.metaEngagementsHint", {
        defaultValue:
          "Post engagements: reactions, comments, shares, saves and clicks on the ad.",
      }),
      placements: t("campaigns.social.placements", {
        defaultValue: "Placements",
      }),
    },
    tiktok: {
      clicks: t("campaigns.clicks", { defaultValue: "Clicks" }),
      conversionsHint: t("campaigns.social.tiktokConversionsHint", {
        defaultValue:
          "Conversions attributed by TikTok to the optimisation event.",
      }),
      engagementsHint: t("campaigns.social.tiktokEngagementsHint", {
        defaultValue: "Likes, comments, shares and follows.",
      }),
      placements: t("campaigns.social.placements", {
        defaultValue: "Placements",
      }),
    },
    reddit: {
      clicks: t("campaigns.clicks", { defaultValue: "Clicks" }),
      conversionsHint: t("campaigns.social.redditConversionsHint", {
        defaultValue: "Key conversions attributed by Reddit.",
      }),
      engagementsHint: undefined,
      placements: t("campaigns.social.communities", {
        defaultValue: "Communities",
      }),
    },
  }[network];

  const currency = mergedCampaignData?.currency_enum;
  const campaignStartDate = mergedCampaignData?.startDate
    ? dayjs(mergedCampaignData.startDate).format("YYYY-MM-DD")
    : "";
  const campaignEndDate = mergedCampaignData?.endDate
    ? dayjs(mergedCampaignData.endDate).format("YYYY-MM-DD")
    : "";

  const summary = mergedCampaignData?.metrics?.summary;
  const rawDailyData = mergedCampaignData?.metrics?.dailyData;
  const dailyData = useMemo(() => rawDailyData ?? [], [rawDailyData]);
  const rawMetrics = useMemo<CampaignMetricsDTO[]>(() => {
    const metrics = mergedCampaignData?.metrics as
      | { rawMetrics?: CampaignMetricsDTO[] }
      | undefined;
    return metrics?.rawMetrics ?? [];
  }, [mergedCampaignData?.metrics]);

  const totalCost = summary?.totalCost ?? 0;
  const totalImpressions = summary?.totalImpressions ?? 0;
  const totalClicks = summary?.totalClicks ?? 0;
  const totalConversions = summary?.totalConversions ?? 0;
  const totalEngagements = summary?.totalEngagements ?? 0;
  const totalCompleteViews = summary?.totalCompleteViews ?? 0;
  // Reproducciones iniciadas; si la red no las da, las completas.
  const videoViews = summary?.totalVideoViews || totalCompleteViews;
  const hasVideo = videoViews > 0;
  const hasEngagements = totalEngagements > 0;
  const hasReach = summary?.reachScope === "EXACT" && summary.uniqueUsers > 0;

  const timelineData: MetricDataPoint[] = useMemo(
    () =>
      dailyData.map((day) => ({
        date: day.date,
        ctr: day.ctr ?? 0,
        cpc: day.cpc ?? 0,
        cpm: day.cpm ?? 0,
        impressions: day.impressions ?? 0,
        clicks: day.clicks ?? 0,
        cost: day.cost ?? 0,
        completeViews: day.completeViews ?? 0,
        vtr: day.vtr ?? 0,
      })),
    [dailyData]
  );
  const hiddenChartMetrics = useMemo<MetricType[]>(
    () => (hasVideo ? [] : ["completeViews", "vtr"]),
    [hasVideo]
  );
  const chartLabels = useMemo(() => ({ clicks: copy.clicks }), [copy.clicks]);

  const campaignRows = useMemo(() => rowsByCampaign(rawMetrics), [rawMetrics]);
  const dailyRows = useMemo(
    () => rowsByDay(rawMetrics, (date) => dayjs(date).format("DD/MM/YYYY")),
    [rawMetrics]
  );

  const notAvailable = t("common.notAvailable", { defaultValue: "N/A" });
  const money = (value: number | null) =>
    value === null ? notAvailable : formatCurrency(value, currency);

  if (isLoadingMergedCampaign) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const paperSx = {
    p: 3,
    borderRadius: 2,
    border: `1px solid ${theme.palette.grey[200]}`,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Paper elevation={0} sx={paperSx}>
        <MetricsDateToolbar
          startDate={metricsStartDate ?? ""}
          endDate={metricsEndDate ?? ""}
          campaignStartDate={campaignStartDate}
          campaignEndDate={campaignEndDate}
          onStartDateChange={onMetricsStartDateChange}
          onEndDateChange={onMetricsEndDateChange}
          onClear={onClearMetricsDateFilters}
          onExport={() =>
            exportDailyMetrics(
              dailyData,
              mergedCampaignData?.name || network,
              t,
              dispatch
            )
          }
          exportDisabled={dailyData.length === 0}
        />

        <Divider sx={{ mb: 3 }} />

        {!summary || totalImpressions === 0 ? (
          <Typography variant="body1" color="text.secondary">
            {t("campaigns.noMetricsData", {
              defaultValue: "No metrics available for this campaign.",
            })}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box>
              <SectionTitle
                title={t("campaigns.social.delivery", {
                  defaultValue: "Delivery and reach",
                })}
                hint={
                  hasReach
                    ? t("campaigns.googleAds.reachHint", {
                        defaultValue:
                          "Reach and frequency cover the whole campaign: unique users cannot be added up across days.",
                      })
                    : undefined
                }
              />
              <Box sx={cardGridSx}>
                <CampaignDetailsCard
                  value={formatCurrency(totalCost, currency)}
                  label={t("home.dashboardInvestment", {
                    defaultValue: "Investment",
                  })}
                />
                <CampaignDetailsCard
                  value={formatNumber(totalImpressions)}
                  label={t("campaigns.impressions", {
                    defaultValue: "Impressions",
                  })}
                />
                {hasReach && (
                  <CampaignDetailsCard
                    value={formatNumber(summary.uniqueUsers)}
                    label={t("campaigns.reach", { defaultValue: "Reach" })}
                  />
                )}
                {hasReach && summary.frequency != null && (
                  <CampaignDetailsCard
                    value={formatDecimal(summary.frequency)}
                    label={t("campaigns.frequency", {
                      defaultValue: "Frequency",
                    })}
                  />
                )}
                <CampaignDetailsCard
                  value={formatCurrency(summary.avgCPM, currency)}
                  label={t("campaigns.cpm", { defaultValue: "CPM" })}
                />
              </Box>
            </Box>

            <Box>
              <SectionTitle
                title={t("campaigns.social.traffic", {
                  defaultValue: "Traffic",
                })}
              />
              <Box sx={cardGridSx}>
                <CampaignDetailsCard
                  value={formatNumber(totalClicks)}
                  label={copy.clicks}
                />
                <CampaignDetailsCard
                  value={formatPercentage(summary.avgCTR)}
                  label={t("campaigns.ctr", { defaultValue: "CTR" })}
                />
                <CampaignDetailsCard
                  value={money(costPer(totalCost, totalClicks))}
                  label={t("campaigns.googleAds.avgCpc", {
                    defaultValue: "Avg. CPC",
                  })}
                />
              </Box>
            </Box>

            {hasEngagements && (
              <Box>
                <SectionTitle
                  title={t("campaigns.social.engagement", {
                    defaultValue: "Engagement",
                  })}
                  hint={copy.engagementsHint}
                />
                <Box sx={cardGridSx}>
                  <CampaignDetailsCard
                    value={formatNumber(totalEngagements)}
                    label={t("campaigns.social.engagements", {
                      defaultValue: "Engagements",
                    })}
                  />
                  <CampaignDetailsCard
                    value={formatPercentage(
                      ratio(totalEngagements, totalImpressions)
                    )}
                    label={t("campaigns.social.engagementRate", {
                      defaultValue: "Engagement rate",
                    })}
                  />
                  <CampaignDetailsCard
                    value={money(costPer(totalCost, totalEngagements))}
                    label={t("campaigns.social.costPerEngagement", {
                      defaultValue: "Cost / engagement",
                    })}
                  />
                </Box>
              </Box>
            )}

            {hasVideo && (
              <Box>
                <SectionTitle
                  title={t("campaigns.googleAds.video", {
                    defaultValue: "Video",
                  })}
                />
                <Box sx={cardGridSx}>
                  <CampaignDetailsCard
                    value={formatNumber(videoViews)}
                    label={t("campaigns.social.videoPlays", {
                      defaultValue: "Video plays",
                    })}
                  />
                  <CampaignDetailsCard
                    value={formatNumber(totalCompleteViews)}
                    label={t("campaigns.completeViews", {
                      defaultValue: "Complete Views",
                    })}
                  />
                  <CampaignDetailsCard
                    value={formatPercentage(
                      ratio(totalCompleteViews, videoViews)
                    )}
                    label={t("campaigns.social.completionRate", {
                      defaultValue: "Completion rate",
                    })}
                  />
                  <CampaignDetailsCard
                    value={money(costPer(totalCost, videoViews))}
                    label={t("campaigns.social.costPerPlay", {
                      defaultValue: "Cost / play",
                    })}
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <VideoRetention
                    videoViews={videoViews}
                    firstQuartile={summary.totalFirstQuartileViews ?? 0}
                    midpoint={summary.totalMidpointViews ?? 0}
                    thirdQuartile={summary.totalThirdQuartileViews ?? 0}
                    complete={totalCompleteViews}
                  />
                </Box>
              </Box>
            )}

            <Box>
              <SectionTitle
                title={t("campaigns.googleAds.conversions", {
                  defaultValue: "Conversions",
                })}
                hint={copy.conversionsHint}
              />
              <Box sx={cardGridSx}>
                <CampaignDetailsCard
                  value={formatNumber(totalConversions)}
                  label={t("campaigns.totalConversions", {
                    defaultValue: "Conversions",
                  })}
                />
                <CampaignDetailsCard
                  value={formatPercentageNoMultiply(
                    summary.conversionRate ?? 0
                  )}
                  label={t("campaigns.conversionRate", {
                    defaultValue: "Conversion Rate",
                  })}
                />
                <CampaignDetailsCard
                  value={money(costPer(totalCost, totalConversions))}
                  label={t("campaigns.googleAds.costPerConversion", {
                    defaultValue: "Cost / conv.",
                  })}
                />
              </Box>
            </Box>

            <MetricsTimelineChart
              title={t("campaigns.metricsOverTime", {
                defaultValue: "Metrics Over Time",
              })}
              subtitle={t("campaigns.clickMetricsToCompare", {
                defaultValue:
                  "Click on metrics to compare or select 'All' to see accumulated data",
              })}
              data={timelineData}
              isLoading={isLoadingMergedCampaign}
              hiddenMetrics={hiddenChartMetrics}
              metricLabelsOverride={chartLabels}
            />
          </Box>
        )}
      </Paper>

      {campaignRows.length > 0 && (
        <Paper elevation={0} sx={paperSx}>
          <SectionTitle
            title={t("campaigns.googleAds.byCampaign", {
              defaultValue: "Performance by campaign",
            })}
          />
          <PerformanceTable
            rows={campaignRows}
            firstColumn={t("campaigns.social.campaignOrAdSet", {
              defaultValue: "Campaign / ad set",
            })}
            currency={currency}
            showEngagements={hasEngagements}
            showVideoViews={hasVideo}
            clicksLabel={copy.clicks}
          />
        </Paper>
      )}

      {dailyRows.length > 0 && (
        <Paper elevation={0} sx={paperSx}>
          <SectionTitle
            title={t("campaigns.googleAds.daily", {
              defaultValue: "Daily performance",
            })}
          />
          <PerformanceTable
            rows={dailyRows}
            firstColumn={t("common.date", { defaultValue: "Date" })}
            currency={currency}
            showEngagements={hasEngagements}
            showVideoViews={hasVideo}
            clicksLabel={copy.clicks}
          />
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
        {t("campaigns.social.lifetimeHint", {
          defaultValue:
            "Ads and placements show the totals of the whole campaign, not the selected dates.",
        })}
      </Typography>

      <CreativeMetricsSection
        mergedCampaignId={mergedCampaignId}
        campaignName={mergedCampaignData?.name}
        currency={currency}
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        title={t("campaigns.social.ads", { defaultValue: "Ads" })}
      />

      <PublisherMetricsSection
        mergedCampaignId={mergedCampaignId}
        campaignName={mergedCampaignData?.name}
        currency={currency}
        startDate={campaignStartDate}
        endDate={campaignEndDate}
        title={copy.placements}
      />
    </Box>
  );
};

export default SocialMergeCampaignDetails;
